import {
  Badge,
  Button,
  Card,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Link,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  TableColumnDefinition,
  Text,
  tokens,
} from "@fluentui/react-components";
import { Dismiss20Regular, Search20Regular } from "@fluentui/react-icons";
import { useApiGet } from "@skanska/vsa-client-auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useCloneToolDraft,
  useDoesToolHaveDraft,
  useRelinkToolFile,
} from "../../hooks/useTools";
import { ApiEndpoints } from "../../models/ApiEndpoints";
import { ClientRoutes } from "../../models/ClientRoutes";
import PagedResult from "../../models/PagedResult";
import { DocumentHealthCheckItem, Tool } from "../../models/Tool";
import { ButtonWithLoading } from "../ButtonWithLoading";

const useStyles = makeStyles({
  dialogSurface: {
    minWidth: "750px",
    maxWidth: "900px",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  orphanInfoCard: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  infoRow: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
  },
  infoLabel: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: "120px",
  },
  cellWrap: {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
  searchContainer: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    alignItems: "flex-end",
  },
  searchInput: {
    minWidth: "350px",
    flex: 1,
  },
  selectedToolCard: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  selectedToolHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  clickableRow: {
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  resultsContainer: {
    maxHeight: "260px",
    overflowY: "auto",
  },
});

export interface LinkOrphanToDraftDialogProps {
  orphanItem: DocumentHealthCheckItem | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: (message: string) => void;
}

export const LinkOrphanToDraftDialog = ({
  orphanItem,
  open,
  onClose,
  onSuccess,
}: LinkOrphanToDraftDialogProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: searchResults, isLoading: searchLoading } = useApiGet<
    PagedResult<Tool>
  >(
    `${ApiEndpoints.ToolAdminSearchDocuments}?query=${encodeURIComponent(activeQuery)}&page=1&pageSize=10`,
    null,
    `admin-doc-search-orphan-${activeQuery}`,
    { enabled: Boolean(activeQuery) }
  );

  const { data: draftId, isLoading: loadingHasDraft } = useDoesToolHaveDraft(
    selectedTool?.toolId ?? "",
    { enabled: Boolean(selectedTool?.toolId) }
  );

  const { mutateAsync: cloneToolDraft } = useCloneToolDraft(
    selectedTool?.toolId ?? ""
  );
  const { mutateAsync: relinkFile } = useRelinkToolFile(
    selectedTool?.toolId ?? ""
  );

  const handleClose = () => {
    setSearchQuery("");
    setActiveQuery("");
    setSelectedTool(null);
    setErrorMessage(null);
    setIsCreating(false);
    onClose();
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setActiveQuery(searchQuery.trim());
      setSelectedTool(null);
      setErrorMessage(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSelectTool = (tool: Tool) => {
    setSelectedTool(tool);
    setErrorMessage(null);
  };

  const handleClearSelectedTool = () => {
    setSelectedTool(null);
    setErrorMessage(null);
  };

  const handleLinkAndCreateDraft = async () => {
    if (
      !orphanItem?.spFileUniqueId ||
      !selectedTool?.toolId ||
      !selectedTool?.versionId
    ) {
      return;
    }

    setIsCreating(true);
    setErrorMessage(null);

    try {
      const createdDraft = await cloneToolDraft({
        versionId: selectedTool.versionId,
        oldVersion: !selectedTool.isCurrentVersion,
      });

      await relinkFile({
        newFileUniqueId: orphanItem.spFileUniqueId,
        versionId: createdDraft.versionId,
        sharePointLibrary:
          orphanItem.spLibrary ??
          orphanItem.sharePointLibrary ??
          "PublishedToolVersions",
      });

      const successText = t("documentManagement.health.linkToDraftSuccess", {
        title: selectedTool.title,
      });

      handleClose();
      onSuccess?.(successText);
      navigate(`${ClientRoutes.ToolsDrafts}/${createdDraft.versionId}`);
    } catch {
      setErrorMessage(t("documentManagement.health.linkToDraftError"));
    } finally {
      setIsCreating(false);
    }
  };

  const columns: TableColumnDefinition<Tool>[] = [
    createTableColumn<Tool>({
      columnId: "title",
      renderHeaderCell: () => t("title"),
      renderCell: (item) => item.title,
    }),
    createTableColumn<Tool>({
      columnId: "adminDisplayName",
      renderHeaderCell: () => t("documentManagement.adminName"),
      renderCell: (item) => (
        <span className={classes.cellWrap}>
          {item.adminDisplayName ?? "-"}
        </span>
      ),
    }),
    createTableColumn<Tool>({
      columnId: "version",
      renderHeaderCell: () => t("version"),
      renderCell: (item) => item.version ?? "-",
    }),
    createTableColumn<Tool>({
      columnId: "toolId",
      renderHeaderCell: () => t("documentManagement.toolId"),
      renderCell: (item) => (
        <span className={classes.cellWrap}>{item.toolId ?? ""}</span>
      ),
    }),
    createTableColumn<Tool>({
      columnId: "action",
      renderHeaderCell: () => "",
      renderCell: (item) => (
        <Button size="small" onClick={() => handleSelectTool(item)}>
          {t("select")}
        </Button>
      ),
    }),
  ];

  const hasDraft = Boolean(draftId);

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) handleClose();
      }}
    >
      <DialogSurface className={classes.dialogSurface}>
        <DialogBody>
          <DialogTitle>
            {t("documentManagement.health.linkToDraftDialogTitle")}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {orphanItem && (
              <Card className={classes.orphanInfoCard}>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>{t("title")}:</span>
                  <span>{orphanItem.spTitle ?? "-"}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>
                    {t("documentManagement.health.fileUniqueId")}:
                  </span>
                  <span className={classes.cellWrap}>
                    {orphanItem.spFileUniqueId}
                  </span>
                </div>
                {orphanItem.spLibrary && (
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>
                      {t("documentManagement.health.library")}:
                    </span>
                    <span>{orphanItem.spLibrary}</span>
                  </div>
                )}
                {orphanItem.spWebUrl && (
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>
                      {t("documentManagement.health.url")}:
                    </span>
                    <Link
                      href={orphanItem.spWebUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {orphanItem.spWebUrl}
                    </Link>
                  </div>
                )}
              </Card>
            )}

            {!selectedTool ? (
              <>
                <div className={classes.searchContainer}>
                  <Field
                    label={t("documentManagement.health.searchDocumentToLink")}
                  >
                    <Input
                      className={classes.searchInput}
                      placeholder={t(
                        "documentManagement.health.searchDocumentPlaceholder"
                      )}
                      value={searchQuery}
                      onChange={(_, data) => setSearchQuery(data.value)}
                      onKeyDown={handleKeyDown}
                      contentAfter={<Search20Regular />}
                    />
                  </Field>
                  <ButtonWithLoading
                    onClick={handleSearch}
                    isLoading={searchLoading && !!activeQuery}
                    text={t("documentManagement.search")}
                  />
                </div>

                {searchLoading && activeQuery && (
                  <Spinner label={t("loading")} />
                )}

                {!searchLoading &&
                  activeQuery &&
                  searchResults &&
                  searchResults.items.length > 0 && (
                    <div className={classes.resultsContainer}>
                      <DataGrid
                        items={searchResults.items}
                        columns={columns}
                        getRowId={(item) => item.versionId}
                        resizableColumns
                        columnSizingOptions={{
                          title: { idealWidth: 180, minWidth: 120 },
                          adminDisplayName: {
                            idealWidth: 140,
                            minWidth: 100,
                          },
                          version: { idealWidth: 70, minWidth: 50 },
                          toolId: { idealWidth: 200, minWidth: 120 },
                          action: { idealWidth: 80, minWidth: 70 },
                        }}
                      >
                        <DataGridHeader>
                          <DataGridRow>
                            {({ renderHeaderCell }) => (
                              <DataGridHeaderCell>
                                {renderHeaderCell()}
                              </DataGridHeaderCell>
                            )}
                          </DataGridRow>
                        </DataGridHeader>
                        <DataGridBody<Tool>>
                          {({ item, rowId }) => (
                            <DataGridRow<Tool>
                              key={rowId}
                              className={classes.clickableRow}
                              onClick={() => handleSelectTool(item)}
                            >
                              {({ renderCell }) => (
                                <DataGridCell>{renderCell(item)}</DataGridCell>
                              )}
                            </DataGridRow>
                          )}
                        </DataGridBody>
                      </DataGrid>
                    </div>
                  )}

                {!searchLoading &&
                  activeQuery &&
                  searchResults &&
                  searchResults.items.length === 0 && (
                    <MessageBar intent="warning">
                      <MessageBarBody>
                        {t("documentManagement.noResults", {
                          query: activeQuery,
                        })}
                      </MessageBarBody>
                    </MessageBar>
                  )}
              </>
            ) : (
              <>
                <Card className={classes.selectedToolCard}>
                  <div className={classes.selectedToolHeader}>
                    <Text weight="semibold">
                      {t("documentManagement.health.selectedDocument")}
                    </Text>
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<Dismiss20Regular />}
                      onClick={handleClearSelectedTool}
                    >
                      {t("documentManagement.health.changeSelectedDocument")}
                    </Button>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>{t("title")}:</span>
                    <span>{selectedTool.title}</span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>
                      {t("documentManagement.toolId")}:
                    </span>
                    <span className={classes.cellWrap}>
                      {selectedTool.toolId}
                    </span>
                  </div>
                  <div className={classes.infoRow}>
                    <span className={classes.infoLabel}>{t("version")}:</span>
                    <span>{selectedTool.version ?? "-"}</span>
                  </div>
                  {selectedTool.isCurrentVersion && (
                    <div className={classes.infoRow}>
                      <span className={classes.infoLabel}>{t("status")}:</span>
                      <Badge appearance="filled" color="informative">
                        {t("active")}
                      </Badge>
                    </div>
                  )}
                </Card>

                {loadingHasDraft && (
                  <Spinner
                    size="tiny"
                    label={t("documentManagement.health.checkingDraftStatus")}
                  />
                )}

                {!loadingHasDraft && hasDraft && (
                  <MessageBar intent="warning">
                    <MessageBarBody>
                      <MessageBarTitle>
                        {t("documentManagement.health.hasDraftWarning")}
                      </MessageBarTitle>
                    </MessageBarBody>
                  </MessageBar>
                )}

                {!loadingHasDraft && !hasDraft && (
                  <MessageBar intent="info">
                    <MessageBarBody>
                      {t("documentManagement.health.noDraftInfo")}
                    </MessageBarBody>
                  </MessageBar>
                )}
              </>
            )}

            {errorMessage && (
              <MessageBar intent="error">
                <MessageBarBody>{errorMessage}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              {t("cancel")}
            </Button>
            {selectedTool && !loadingHasDraft && hasDraft && draftId && (
              <Button
                appearance="primary"
                onClick={() => {
                  handleClose();
                  navigate(`${ClientRoutes.ToolsDrafts}/${draftId}`);
                }}
              >
                {t("toDraft")}
              </Button>
            )}
            {selectedTool && !loadingHasDraft && !hasDraft && (
              <ButtonWithLoading
                text={t("documentManagement.health.linkAndCreateDraft")}
                isLoading={isCreating}
                onClick={handleLinkAndCreateDraft}
              />
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default LinkOrphanToDraftDialog;
